// src/timer/TimersWidget.tsx
import * as React from "react";
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Stack,
    TextField,
    Button,
    Tooltip,
    Fab,
    Slide,
    Portal,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import PlayArrowRounded from "@mui/icons-material/PlayArrowRounded";
import PauseRounded from "@mui/icons-material/PauseRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import TimerRounded from "@mui/icons-material/TimerRounded";
import PauseCircleRounded from "@mui/icons-material/PauseCircleRounded";
import PlayCircleRounded from "@mui/icons-material/PlayCircleRounded";
import { useTimers } from "./TimerProvider";
import type { ITimer } from "../types/timer";
import type { RootState } from "../store";
import { useSelector } from "react-redux";
import Theme from "../utils/theme";
import { ExpandMoreRounded } from "@mui/icons-material";

function msToClock(ms: number) {
    const s = Math.ceil(ms / 1000);
    const m = Math.floor(s / 60);
    const r = s % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(r).padStart(2, "0");
    return `${mm}:${ss}`;
}

const TimersWidget: React.FC = () => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = React.useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const {
        timers,
        minimized,
        addTimer,
        removeTimer,
        renameTimer,
        setTimerMinutes,
        setTimerSeconds,
        start,
        pause,
        reset,
        startAll,
        pauseAll,
        resetAll,
        toggleMinimized,
    } = useTimers();

    const [newLabel, setNewLabel] = React.useState("");
    const [newMinutes, setNewMinutes] = React.useState(5);
    const [newSeconds, setNewSeconds] = React.useState(0);

    const running = timers.filter((t: ITimer) => t.running);
    const nextRemaining = running.length ? Math.min(...running.map((t) => t.remainingMs)) : null;

    return (
        <Portal>
            {/* Collapsed FAB */}
            <Slide direction="up" in={minimized} mountOnEnter unmountOnExit>
                <Box sx={{ position: "fixed", right: 0, bottom: 0, zIndex: (t) => t.zIndex.modal + 2 }}>
                    <Tooltip title="Open timers">
                        <Fab
                            variant="extended"
                            onClick={toggleMinimized}
                            aria-label="Open timers"
                            sx={{
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: 2,
                                color: "white",
                                height: "24px",
                            }}
                        >
                            <TimerRounded sx={{ fontSize: "18px" }} />
                            {running.length > 0 ? (
                                <Typography sx={{ display: { xs: "none", sm: "block" }, ml: 1 }} variant="h6">{`${
                                    running.length
                                } running · next ${msToClock(nextRemaining!)}`}</Typography>
                            ) : (
                                <Typography
                                    sx={{ display: { xs: "none", sm: "block" }, ml: 1 }}
                                    variant="caption"
                                >{`Timers (${timers.length})`}</Typography>
                            )}
                        </Fab>
                    </Tooltip>
                </Box>
            </Slide>

            {/* Expanded panel */}
            <Slide direction="up" in={!minimized} mountOnEnter unmountOnExit>
                <Paper
                    elevation={8}
                    aria-label="Timers"
                    sx={{
                        position: "fixed",
                        bottom: 16,
                        right: 16,
                        left: { xs: 16, sm: "auto" },
                        width: { xs: "100%", sm: 420 },
                        maxWidth: "calc(100vw - 32px)",
                        p: 2,
                        borderRadius: 3,
                        zIndex: theme.zIndex.modal + 2,
                        backdropFilter: "blur(6px)",
                        overflowX: "hidden",
                    }}
                >
                    {/* Header */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TimerRounded sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="subtitle1" fontWeight={600}>
                                Timers
                            </Typography>
                        </Stack>
                        <Tooltip title="Minimize">
                            <IconButton onClick={toggleMinimized} aria-label="Minimize timers" size="small">
                                <RemoveRounded />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    {/* Quick add */}
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreRounded />} aria-controls="add-timer-content" id="add-timer-header">
                            <Typography variant="subtitle1" fontWeight={600}>
                                Quick Add
                            </Typography>
                        </AccordionSummary>

                        <AccordionDetails>
                            <Stack direction="column" spacing={1}>
                                <TextField
                                    size="small"
                                    label="Label"
                                    placeholder={`Timer ${timers.length + 1}`}
                                    value={newLabel}
                                    onChange={(e) => setNewLabel(e.target.value)}
                                    fullWidth
                                />
                                <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
                                    <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
                                        <TextField
                                            size="small"
                                            label="Min"
                                            type="number"
                                            inputProps={{ min: 0 }}
                                            value={newMinutes}
                                            onChange={(e) => setNewMinutes(Number(e.target.value || 0))}
                                            sx={{ flex: 1 }}
                                        />
                                        <TextField
                                            size="small"
                                            label="Sec"
                                            type="number"
                                            inputProps={{ min: 0, max: 59 }}
                                            value={newSeconds}
                                            onChange={(e) => setNewSeconds(Number(e.target.value || 0))}
                                            sx={{ flex: 1 }}
                                        />
                                    </Stack>

                                    <Box>
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                addTimer({ label: newLabel, minutes: newMinutes, seconds: newSeconds });
                                                setNewLabel("");
                                            }}
                                            sx={{ width: "100%", whiteSpace: "nowrap", backgroundColor: theme.palette.primary.main }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <AddRounded sx={{ position: "relative", bottom: 1, mr: 0.5 }} />
                                                <Typography variant="subtitle2">Add</Typography>
                                            </Box>
                                        </Button>
                                    </Box>
                                </Stack>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Bulk controls */}
                    {timers.length !== 0 && (
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                mt: 1,
                                flexWrap: "wrap",
                                "& > *": { flex: "1 1 30%" }, // spread on mobile
                            }}
                        >
                            <Button sx={{ color: theme.palette.primary.main }} onClick={startAll} startIcon={<PlayCircleRounded />}>
                                Start all
                            </Button>
                            <Button sx={{ color: theme.palette.primary.main }} onClick={pauseAll} startIcon={<PauseCircleRounded />}>
                                Pause all
                            </Button>
                            <Button sx={{ color: theme.palette.primary.main }} onClick={resetAll} startIcon={<RestartAltRounded />}>
                                Reset all
                            </Button>
                        </Stack>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Timers list */}
                    <Stack spacing={1.25} sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}>
                        {timers
                            .slice()
                            .sort((a: ITimer, b: ITimer) => a.createdAt - b.createdAt)
                            .map((t: ITimer) => {
                                const minutes = Math.floor(t.totalMs / 60_000);
                                const seconds = Math.floor((t.totalMs % 60_000) / 1000);

                                return (
                                    <Paper key={t.id} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                                        <Stack direction="column" alignItems="stretch" spacing={1}>
                                            {/* Clock */}
                                            <Box sx={{ textAlign: "center", width: "100%" }}>
                                                <Typography variant="h3" sx={{ fontVariantNumeric: "tabular-nums", lineHeight: 1.2 }}>
                                                    {msToClock(t.remainingMs)}
                                                </Typography>
                                                {!t.running && t.remainingMs === 0 && (
                                                    <Typography variant="caption" color="error.main">
                                                        Done
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Label */}
                                            <TextField
                                                size="small"
                                                label="Label"
                                                value={t.label}
                                                onChange={(e) => renameTimer(t.id, e.target.value)}
                                                fullWidth
                                            />

                                            {/* Controls: wrap to new lines on mobile */}
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                useFlexGap
                                                flexWrap="wrap"
                                                alignItems="center"
                                                sx={{ flex: 1 }}
                                            >
                                                <TextField
                                                    size="small"
                                                    label="Min"
                                                    type="number"
                                                    inputProps={{ min: 0 }}
                                                    value={minutes}
                                                    onChange={(e) => setTimerMinutes(t.id, Number(e.target.value || 0))}
                                                    sx={{ flex: "1 1 110px" }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="Sec"
                                                    type="number"
                                                    inputProps={{ min: 0, max: 59 }}
                                                    value={seconds}
                                                    onChange={(e) => setTimerSeconds(t.id, Number(e.target.value || 0))}
                                                    sx={{ flex: "1 1 110px" }}
                                                />

                                                <Stack
                                                    direction="row"
                                                    spacing={0.5}
                                                    sx={{
                                                        ml: { sm: "auto" },
                                                        flexShrink: 0,
                                                        width: "100%",
                                                        justifyContent: "flex-end",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {t.running ? (
                                                        <Tooltip title="Pause">
                                                            <IconButton onClick={() => pause(t.id)} aria-label="Pause timer">
                                                                <PauseRounded />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Start">
                                                            <Box>
                                                                <Button
                                                                    disabled={t.remainingMs <= 0}
                                                                    variant="contained"
                                                                    onClick={() => start(t.id)}
                                                                    sx={{
                                                                        width: "100%",
                                                                        whiteSpace: "nowrap",
                                                                        backgroundColor: theme.palette.primary.main,
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            justifyContent: "center",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <PlayArrowRounded
                                                                            sx={{ position: "relative", bottom: 1, mr: 0.5 }}
                                                                        />
                                                                        <Typography variant="subtitle2">Start</Typography>
                                                                    </Box>
                                                                </Button>
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Reset">
                                                        <IconButton onClick={() => reset(t.id)} aria-label="Reset timer">
                                                            <RestartAltRounded />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={() => removeTimer(t.id)} aria-label="Delete timer">
                                                            <DeleteRounded />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                    </Stack>
                </Paper>
            </Slide>
        </Portal>
    );
};

export default TimersWidget;
